<template>
    <div class="account-overview-page-container">
        <div v-if="subscriptionLinkingOnly" class="subscription-linking-contaier content-container">
            <div class="linking-info">
                <label>{{ $t('overview.linking-info') }}</label>
            </div>

            <div v-if="roles.newTickets" class="new-contract-wrapper">
                <button id="bmV3LWNvbnRyYWN0" class="new-contract" @click="goToAnlageAssistent">
                    <label>{{ $t('overview.new-contract') }}</label>
                </button>
            </div>

            <div class="link-subscription-wrapper">
                <button id="bGluay1zdWI=" class="link-subscription" @click="prepareLinkSubscription">
                    <label>{{ $t('overview.link-subscription') }}</label>
                </button>
            </div>
        </div>
        <div v-else class="content-container">
            <div class="customer-information-container">
                <div class="customer-information-wrapper">
                    <h2>{{ $t('overview.customer') }} {{ customer.customerNr }}</h2>

                    <CustomerOverview :customer="customer"/>

                    <div class="edit-wrapper" v-if="roles.editPersonalData">
                        <button id="ZWRpdC1jdXN0b21lcg==" class="edit" @click="goToCustomerData">
                            <img src="@img/pen-solid-bright.svg"/>
                        </button>
                    </div>
                </div>

                <div v-if="roles.displayBankData" class="account-owner-information-wrapper">
                    <h2>{{ $t('overview.bank-details') }}</h2>

                    <BankAccountOwnerOverview :customer="bankAccountOwner" />

                    <div class="edit-wrapper" v-if="roles.editPersonalData">
                        <button id="ZWRpdC1iYW5rLWE=" class="edit" @click="goToBankAccountOwnerData">
                            <img src="@img/pen-solid-bright.svg"/>
                        </button>
                    </div>
                </div>

                <div v-if="roles.newTickets" class="new-contract-wrapper">
                    <button id="bmV3LWNvbnRyYWN0" class="new-contract" @click="goToAnlageAssistent">
                        <label>{{ $t('overview.new-contract') }}</label>
                    </button>
                </div>
            </div>

            <div class="contracts-container">
                <Accordion class="entry" :initially-open="idx === 0" v-for="(contract, idx) in contracts" :key="idx" :title="`${contract.name} - ${contract.cardOwner.firstName} ${contract.cardOwner.lastName}`">
                    <template v-slot:icon>
                        <img src="@img/angle-down-solid.svg">
                    </template>

                    <div class="authorization-information">
                        <AuthorizationOverview :contract="contract"/>
                    </div>

                    <div class="customer-information">
                        <CardOwnerOverview :customer="contract.cardOwner"/>

                        <div class="edit-wrapper">
                            <button class="edit" @click="goToCardOwnerData(contract.cardOwner.id)">
                                <img src="@img/pen-solid-bright.svg"/>
                            </button>
                        </div>
                    </div>

                    <div v-if="!contract.terminationComplete" class="authorization-actions">
                        <button v-if="roles.certificationUpload && contract.confirmationNeeded" class="button active" @click="handleUploadProofOpen(contract.cardOwner.id)">
                            <label>{{ $t('overview.upload-certification') }}</label>
                        </button>
                        <button v-if="roles.replacement && contract.replaceable" class="button active" @click="prepareReplaceTicket(contract.id)">
                            <label>{{ $t('overview.order-replacement-card') }}</label>
                        </button>
                        <button v-if="roles.termination" class="button active" @click="prepareTerminateTicket(contract)">
                            <label>{{ $t('overview.terminate-contract') }}</label>
                        </button>
                    </div>
                </Accordion>
            </div>
        </div>

        <PopUp class="popup" :title="$t('overview.popups.terminate-contract')" :open="showTerminateTicket" @close="handleTerminateTicketClose" :submitLabel="$t('overview.popups.submit-termination-request')" :cancelLabel="$t('overview.popups.cancel')">
            <DropdownInput :label="$t('overview.popups.reason')" :required="requiredTerminationReason" :option-list="baseData.terminationReasons" :model-value="termination.reason.displayid || -1" @update:model-value="event => termination.reason = event" :error="validator.termination.reason.displayid.$error"/>
            <DateInput class="highlight-allowed-dates" :label="$t('overview.popups.terminate-on')" :required="requiredTerminationDate" :clearable="!requiredTerminationDate" :min-date="new Date()" v-model="termination.date" :allowed-dates="termination.allowedDates" :error="validator.termination.date.$error"/>
            <TextareaInput :label="$t('overview.popups.comment')" v-model="termination.comment"/>
            <span class="required-footnote" v-show="requiredTerminationFootnote">{{ $t('general.required-field') }}</span>
        </PopUp>

        <PopUp class="popup" :title="$t('overview.popups.order-replacement-card')" :open="showReplaceTicket" @close="handleReplaceTicketClose" :submitLabel="$t('overview.popups.submit-replacement-request')" :cancelLabel="$t('overview.popups.cancel')">
            <DropdownInput :label="$t('overview.popups.reason')" :required="requiredReplacementReason" :option-list="baseData.replacementReasons" :model-value="replacement.reason.displayid || -1" @update:model-value="event => replacement.reason = event" :error="validator.replacement.reason.displayid.$error"/>
            <TextareaInput :label="$t('overview.popups.comment')" v-model="replacement.comment"/>
            <span class="required-footnote" v-show="requiredReplacementFootnote"> {{ $t('general.required-field') }}</span>
        </PopUp>

        <PopUp class="popup upload-proof" :title="$t('overview.popups.upload-certification')" :open="showUploadProof" @close="handleUploadProofClose" :submitLabel="$t('overview.popups.submit-certification')" :cancelLabel="$t('overview.popups.cancel')">
            <DragAndDropUpload v-model="uploadedProof" :uploadText="$t('overview.popups.upload-info')" :resultListTitle="$t('overview.popups.uploaded-certification')" :uploadLimit="1" :fileLimit="1" :acceptedFileTypes="'bmp, jpeg, jpg, pdf, png'"/>
        </PopUp>

        <PopUp class="popup link-subscription" :title="$t('overview.popups.link-subscription')" :open="showLinkSubscription" @close="handleLinkSubscriptionClose" :submitLabel="$t('overview.popups.submit-linking')" :cancelLabel="$t('overview.popups.cancel')">
            <label class="info">{{ $t('overview.popups.link-subscription-hint') }}</label>

            <TextInput class="input" :label="$t('overview.popups.customer-nr')" v-model="linkSubscription.customerNr" :required="requiredlinkSubscriptionCustomerNr" :error="validator.linkSubscription.customerNr.$error"/>
            <TextInput class="input" :label="$t('overview.popups.firstname')" v-model="linkSubscription.firstName" :required="requiredlinkSubscriptionFirstName" :error="validator.linkSubscription.firstName.$error"/>
            <TextInput class="input" :label="$t('overview.popups.lastname')" v-model="linkSubscription.lastName" :required="requiredlinkSubscriptionLastName" :error="validator.linkSubscription.lastName.$error"/>
            <DateInput class="input" :label="$t('overview.popups.birthdate')" v-model="linkSubscription.birthdate" :required="requiredlinkSubscriptionBirthdate" :error="validator.linkSubscription.birthdate.$error"/>
            <span class="required-footnote">{{ $t('general.required-field') }}</span>
        </PopUp>

        <PopUp :title="$t('overview.popups.action-successfully-executed')" :open="showSuccess" :submit-label="$t('overview.popups.action-successfully-executed-ok')" :disableCancel="true" @close="handleCloseSuccess">
            <label>{{ $t('overview.popups.action-successfully-executed-text') }}</label>
        </PopUp>
    </div>
</template>

<script>
import axios from 'axios'

import PopUp from '@elements/PopUp.vue'
import TextInput from '@elements/TextInput.vue'
import DateInput from '@elements/DateInput.vue'
import Accordion from '@elements/Accordion.vue'
import TextareaInput from '@elements/TextareaInput.vue'
import DropdownInput from '@elements/DropdownInput.vue'
import CustomerOverview from '@elements/CustomerOverview.vue'
import CardOwnerOverview from '@elements/CardOwnerOverview.vue'
import DragAndDropUpload from '@elements/DragAndDropUpload.vue'
import AuthorizationOverview from '@elements/AuthorizationOverview.vue'
import BankAccountOwnerOverview from '@elements/BankAccountOwnerOverview.vue'
import { useVuelidate } from '@vuelidate/core'
import { minValue, requiredIf, required } from '@vuelidate/validators'

export default {
    name: 'account-overview-page',
    inject: ['keycloak', 'middlewarePortalFunctions', 'util', 'session', 'baseData', 'globalState','toast'],
    setup() {
        return {
            validator: useVuelidate()
        }
    },
    data() {
        return {
            termination: {
                contractId: undefined,
                date: undefined,
                reason: {},
                comment: undefined,
                allowedDates: [], // is updated when the selection changes
                firstName:  undefined,
                lastName:   undefined,
                email:      undefined
            },
            replacement: {
                contractId: undefined,
                reason: {},
                comment: undefined
            },
            linkSubscription: {
                customerNr: undefined,
                firstName:  undefined,
                lastName:   undefined,
                birthdate:  undefined
            },
            roles:{
                newTickets: false,
                displayBankData: false,
                editPersonalData: false,
                certificationUpload: false,
                replacement: false,
                termination: false
            },
            showTerminateTicket: false,
            showReplaceTicket: false,
            showUploadProof: false,
            uploadedProof: [],
            cardOwnerIdForUploadProof: undefined,
            redirectUrl: undefined,
            showLinkSubscription: false,
            showSuccess: false,
            requiredTerminationDate: this.$isRequired.get('terminate-contract.date'),
            requiredTerminationReason: this.$isRequired.get('terminate-contract.reason'),
            requiredReplacementReason: this.$isRequired.get('order-replacement-card.reason'),

            requiredTerminationFootnote: this.$isRequired.some('terminate-contract'),
            requiredReplacementFootnote: this.$isRequired.some('order-replacement-card'),

            requiredlinkSubscriptionCustomerNr: this.$isRequired.getNotFalse('link-subscription.customer-nr'),
            requiredlinkSubscriptionFirstName: this.$isRequired.getNotFalse('link-subscription.first-name'),
            requiredlinkSubscriptionLastName: this.$isRequired.getNotFalse('link-subscription.last-name'),
            requiredlinkSubscriptionBirthdate: this.$isRequired.getNotFalse('link-subscription.birthdate')
        }
    },
    validations() {
        return {
            termination: {
                date: { requiredIf: requiredIf(this.requiredTerminationDate), $autoDirty: true },
                reason: { 
                    // for dropdowns: -1 is nothing selected
                    displayid: {minValue: minValue(this.requiredTerminationReason ? 0 : -1), $autoDirty: true}
                }
            },
            replacement: {
                reason: { 
                    displayid: {minValue: minValue(this.requiredReplacementReason ? 0 : -1), $autoDirty: true}
                }
            },
            linkSubscription: {
                customerNr: { requiredIf: requiredIf(this.requiredlinkSubscriptionCustomerNr), $autoDirty: true },
                firstName:  { requiredIf: requiredIf(this.requiredlinkSubscriptionFirstName), $autoDirty: true },
                lastName:   { requiredIf: requiredIf(this.requiredlinkSubscriptionLastName), $autoDirty: true },
                birthdate:  { requiredIf: requiredIf(this.requiredlinkSubscriptionBirthdate), $autoDirty: true }
            },
            uploadedProof: {required, $autoDirty: true }
        }
    },
    mounted() {
        this.init()
    },
    computed: {
        subscriptionLinkingOnly() {
            return this.globalState.subscriptionLinkingOnly
        },
        customer() {
            return this.globalState.customer
        },
        bankAccountOwner() {
            return this.globalState.bankAccountOwner 
        },
        contracts() {
            return  this.globalState.contracts
        }                     
    },
    methods: {
        init() {
            this.globalState.isLoading = true

            //set roles
            const clientId = this.keycloak?.clientId
            const roles = this.keycloak.resourceAccess[clientId]?.roles || []
            const middlewarePortalFunctions = this.middlewarePortalFunctions?.roles || []
            if (middlewarePortalFunctions || (clientId && Object.keys(this.keycloak.resourceAccess).includes(clientId))) {
                this.roles.newTickets =          roles.includes('new-tickets')          || middlewarePortalFunctions.includes('new-tickets')
                this.roles.displayBankData =     roles.includes('display-bank-data')    || middlewarePortalFunctions.includes('display-bank-data')
                this.roles.editPersonalData =    roles.includes('edit-personal-data')   || middlewarePortalFunctions.includes('edit-personal-data')
                this.roles.certificationUpload = roles.includes('certification-upload') || middlewarePortalFunctions.includes('certification-upload')
                this.roles.replacement =         roles.includes('replacement')          || middlewarePortalFunctions.includes('replacement')
                this.roles.termination =         roles.includes('termination')          || middlewarePortalFunctions.includes('termination')
            }

            // get the redirect url
            let getAnlageAssistentUrl = this.util.middleware()
            getAnlageAssistentUrl += '/session/url-anlageassistent'
            getAnlageAssistentUrl += '?client=' + this.session.client
            getAnlageAssistentUrl += '&corporation=' + this.session.corporationId
            axios.get(getAnlageAssistentUrl)
            .then(response => {
                this.redirectUrl = response.data
                this.globalState.isLoading = false
            })
            .catch(() => {
                this.globalState.isLoading = false
                const errorMessage = this.$t('general.error')
                this.toast.error(errorMessage) 
            })
        },
        goToCustomerData() {
            this.$router.push({ name: 'customer-data', params: { id: this.customer.id }})
        },
        goToBankAccountOwnerData() {
            this.$router.push({ name: 'bank-account-owner-data', params: { id: this.bankAccountOwner.person.id }})
        },
        goToCardOwnerData(id) {
            this.$router.push({ name: 'card-owner-data', params: { id }})
        },
        prepareLinkSubscription() {
            this.showLinkSubscription = true
        },
        prepareReplaceTicket(contractId) {
            this.replacement.contractId = contractId
            this.showReplaceTicket = true
        },
        prepareTerminateTicket(contract) {
            // get possible cancellation days
            let dateUrl = this.util.middleware()
            dateUrl += '/clients/' + this.session.clientId
            dateUrl += '/cancellation-dates/' + contract.productConfigurationId
            dateUrl += '?startDate=' + contract.startDate

            axios.get(dateUrl)
            .then(response => {
                console.log('dates', response.data.data.dateList)
                this.termination.allowedDates = response.data.data?.dateList?.map(timestamp => new Date(timestamp))

                this.termination.date = this.termination.allowedDates?.at(0) || new Date()
                
                this.termination.contractId = contract.id
                this.showTerminateTicket = true

                this.termination.lastName  = this.customer.lastName
                this.termination.firstName = this.customer.firstName
                this.termination.email     = this.customer.email

            })
            .catch(() => {
                const errorMessage = this.$t('general.error')
                this.toast.error(errorMessage) 
            })
        },
        handleLinkSubscriptionClose(submit) {
            if (!submit) {
                this.resetLinkSubscription()
            } else {
                this.validator.linkSubscription.$validate()
                if (this.validator.linkSubscription.$invalid) {
                    return
                }

                try {
                    this.requestLinkSubscription()        
                } catch (error) {
                    const errorMessage = this.$t('general.error')
                    this.toast.error(errorMessage)                    
                }
            }
            this.validator.$reset()
        },
        resetLinkSubscription() {
            this.linkSubscription.customerNr = undefined
            this.linkSubscription.firstName  = undefined
            this.linkSubscription.lastName   = undefined
            this.linkSubscription.birthdate  = undefined
            this.showLinkSubscription        = false
        },
        handleTerminateTicketClose(submit) {
            if (!submit) {
                this.resetTerminateTicket()
            } else {
                this.validator.termination.$touch()
                if (this.validator.termination.$invalid) {
                    return
                }
                try {
                    this.requestTermination()   
                } catch (error) {
                    const errorMessage = this.$t('general.error')
                    this.toast.error(errorMessage)   
                }
            }
            this.validator.$reset()
        },
        resetTerminateTicket() {
            this.termination.reason   = {}
            this.termination.comment = undefined
            this.termination.date    = undefined
            this.showTerminateTicket = false
        },
        handleReplaceTicketClose(submit) {
            if (!submit) {
                this.resetReplaceTicket()
            } else {
                this.validator.replacement.$touch()
                if (this.validator.replacement.$invalid) {
                    return
                }
                try {
                    this.requestReplacement()   
                } catch (error) {
                    const errorMessage = this.$t('general.error')
                    this.toast.error(errorMessage)   
                }
            }
            this.validator.$reset()
        },
        resetReplaceTicket() {
            this.replacement.reason  = {}
            this.replacement.comment = undefined
            this.showReplaceTicket   = false
        },
        requestLinkSubscription() {
            this.globalState.isLoading = true

            let linkSubUrl = this.util.middleware()
            linkSubUrl += '/customers/' + this.linkSubscription.customerNr
            linkSubUrl += '/access'

            const linkSubBody = {
                userToken: this.keycloak.npsUserToken,
                firstName: this.linkSubscription.firstName,
                lastName:  this.linkSubscription.lastName,
                //convert value to UTC timezone
                birthdate: this.util.convertLocalToUtcDate(this.linkSubscription.birthdate),
                email:     this.keycloak.tokenParsed.email
            }

            axios.put(linkSubUrl, linkSubBody)
            .then(response => {
                if(response) {
                    this.globalState.isLoading = false
                    this.resetLinkSubscription()
                    this.showSuccess = true
                }
            })
            .catch(err => {
                this.globalState.isLoading = false
                throw err
            })
        },
        requestTermination() {
            this.globalState.isLoading = true

            let terminationUrl = this.util.middleware()
            terminationUrl += '/contracts/' + this.termination.contractId
            terminationUrl += '/terminatations'

            axios.put(terminationUrl, this.termination)
            .then(() => {
                this.globalState.isLoading = false
                this.resetTerminateTicket()
                this.showSuccess = true
            })
            .catch(err => {
                this.globalState.isLoading = false
                throw err
            })
        },
        requestReplacement() {
            this.globalState.isLoading = true

            let replacementUrl = this.util.middleware()
            replacementUrl += '/contracts/' + this.replacement.contractId
            replacementUrl += '/replacements'

            axios.put(replacementUrl, this.replacement)
            .then(() => {
                this.globalState.isLoading = false
                this.resetReplaceTicket()
                this.showSuccess = true
            })
            .catch(err => {
                this.globalState.isLoading = false
                throw err
            })
        },
        handleUploadProofOpen(cardOwnerId) {
            this.uploadedProof = []
            this.cardOwnerIdForUploadProof = cardOwnerId
            this.showUploadProof = true
        },
        handleUploadProofClose(submit) {
            if (!submit) {
                this.resetUploadProof()
            } else {
                this.validator.uploadedProof.$touch()
                if (this.validator.uploadedProof.$invalid) {
                    return
                }
                try {
                    this.uploadProof()   
                } catch (error) {
                    const errorMessage = this.$t('general.error')
                    this.toast.error(errorMessage)
                }
            }
        },
        async uploadProof() {
            this.globalState.isLoading = true

            let uploadProofUrl = this.util.middleware()
            uploadProofUrl += '/rest/card-owners/' + this.cardOwnerIdForUploadProof
            uploadProofUrl += '/certifications'

            const uploadedProofBody = {
                dataUri: await this.util.convertFileToDataUri(this.uploadedProof[0]),
                fileName: this.uploadedProof[0].name
            }

            axios.post(uploadProofUrl, uploadedProofBody)
            .then(() => {
                this.globalState.isLoading = false
                this.resetUploadProof()
                this.showSuccess = true
            })
            .catch(err => {
                this.globalState.isLoading = false
                throw err
            })
        },
        resetUploadProof() {
            this.uploadedProof = []
            this.cardOwnerIdForUploadProof = undefined
            this.showUploadProof = false
        },
        goToAnlageAssistent() {
            let urlWithUser
            if (this.subscriptionLinkingOnly) {
                urlWithUser = this.redirectUrl + '&register=' + this.keycloak.tokenParsed.sub
            } else {
                urlWithUser = this.redirectUrl + '&token=' + encodeURIComponent(this.keycloak.npsUserToken.replaceAll('+', '-').replaceAll('/', '_'))
            }
            location.replace(urlWithUser)

            
        },
        handleCloseSuccess() {
            this.showSuccess = false
        }
    },
    components: {
        CustomerOverview,
        Accordion,
        AuthorizationOverview,
        PopUp,
        DateInput,
        DragAndDropUpload,
        BankAccountOwnerOverview,
        DropdownInput,
        TextareaInput,
        CardOwnerOverview,
        TextInput
    }
}
</script>

<style lang="scss" scoped>
@use '@css/mixins/required';
.account-overview-page-container {

    > .popup {
        @include required.footnote;
        &.link-subscription {
            :deep(.input) {
                &:first-of-type {
                    margin-top: 20px;
                }
            }
        }

        :deep(.content-container) {
            width: 90%;

            @media(min-width: 992px) {
                max-width: 600px;
            }
        }
    }

    > .content-container.subscription-linking-contaier {
        flex-direction: column;
        background-color: #fff;
        box-shadow: 0 2px 8px #00000022;
        border-radius: 8px;
        padding: 64px 32px;
        margin: 0 auto;

        > .linking-info {
            text-align: center;
            font-size: 2em;
            white-space: pre-wrap;
        }

        > .new-contract-wrapper {
            margin-top: 40px;
            > button.new-contract {
                width: 80%;
                height: 60px;
                margin: 0 auto;
                text-transform: uppercase;
                border-radius: 8px;
                font-weight: bold;
                font-size: 1.75em;
                display: flex;
                align-items: center;
                justify-content: space-around;
                box-shadow: 0 2px 8px #00000022;

                background-color: #4e9c81;
                color: #fff;

                &:hover {
                    background: #4e9c8166;
                }
            }
        }

        > .link-subscription-wrapper {
            margin-top: 20px;

            > button.link-subscription {
                width: 80%;
                height: 60px;
                margin: 0 auto;
                text-transform: uppercase;
                border-radius: 8px;
                font-weight: bold;
                font-size: 1.5em;
                display: flex;
                align-items: center;
                justify-content: space-around;
                box-shadow: 0 2px 8px #00000022;

                background-color: #fff;
                color: #4e9c81;
                border: 1px solid #4e9c81;

                &:hover {
                    background: #eee;
                }
            }
        }

        @media(min-width: 992px) {
            max-width: 600px;
        }
    }

    > .content-container {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;

        > .customer-information-container {
            display: flex;
            flex-direction: column;
            :deep(.new-contract-wrapper) {
                order: -1;
                margin: 0 0 20px 0;
            }
        }

        > .contracts-container {
            flex: 1;
            // overflow-y: auto; // disable on #app-content, to enable just contracts scrolling
            // but then the content-container needs this: height = total height - header - subPageNav - (?? no idea yet where the 12px come from)
            height: 100%;
            padding-bottom: 16px;
        }

        @media(min-width: 992px) {
            flex-direction: row;

            > .customer-information-container {
                width: 300px;
                margin-right: 4em;

                :deep(.new-contract-wrapper) {
                    order: unset;
                    margin: 20px 0 0 0;
                }
            }
        }
    }
}

.customer-information-container {
    > .customer-information-wrapper,
    > .account-owner-information-wrapper {
        width: 100%;
        background-color: #fff;
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 2px 8px #00000022;
        overflow: hidden;
        position: relative;

        &:not(:first-child) {
            margin-top: 20px;
        }

        > h2 {
            margin: 0;
        }

        > div {
            margin-top: 16px;
        }
    }

    > .new-contract-wrapper {
        margin-top: 20px;

        > button.new-contract {
            width: 100%;
            height: 75px;
            text-transform: uppercase;
            border-radius: 8px;
            font-weight: bold;
            font-size: 2em;
            display: flex;
            align-items: center;
            justify-content: space-around;
            box-shadow: 0 2px 8px #00000022;

            background-color: #4e9c81;
            color: #fff;

            &:hover {
               background: #4e9c8166;
            }
        }
    }
}

.contracts-container {
    margin-top: 20px;

        @media(min-width: 992px) {
            margin-top: initial;
        }

    > .entry {
        &:not(:first-of-type) {
            margin-top: 20px;
        }

        :deep(.customer-information) {
            position: relative;
            overflow: hidden;
            border-radius: 8px;
            box-shadow: 0 2px 8px #00000022;
            padding: 16px;

            h3 {
                margin: 0;
                padding: 0;
                margin-bottom: 12px;
            }
        }

        :deep(.authorization-information) {
            margin-bottom: 20px;
        }

        :deep(.authorization-actions) {
            margin-top: 20px;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;

            > .button {
                min-width: 100%;
            }

            > button:nth-of-type(2) {
                margin-top: 10px;
            }
            
            > button:nth-of-type(3) {
                margin-top: 10px;
            }

            @media(min-width: 492px) {
                flex-wrap: nowrap;
                > .button {
                    min-width: 140px
                }

                > button:nth-of-type(2) {
                    margin-top: 0px;
                    margin-left: 8px;
                }
                
                > button:nth-of-type(3) {
                    margin-top: 0px;
                    margin-left: 8px;
                }
            }
        }
    }
}

.edit-wrapper {
    position: absolute;
    right: -16px;
    bottom: -16px;
    border-top-left-radius: 8px;
    overflow: hidden;
    transition: all .2s ease;

    > button.edit {
        height: 50px;
        width: 50px;
        padding: 8px;
        display: flex;
        transition: inherit;
        background-color: #ccc;

        > img {
            transition: inherit;
            height: 20px;
            width: 20px;
        }
    }

    &:hover {
        right: 0;
        bottom: 0;

        > button.edit {
            padding: 12px;
            background-color: #162c99;

            > img {
                height: 30px;
                width: 30px;
            }
        }
    }
}
</style>